import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useLocale } from "@/contexts/LocaleContext";

export type CatalogProduct = Tables<"catalog_products">;
export type CatalogBlock = Tables<"catalog_blocks">;
export type CatalogFile = Tables<"catalog_files">;

// ── Products ──
export const useCatalogProducts = () => {
  const { language } = useLocale();
  return useQuery({
    queryKey: ["catalog-products", language],
    staleTime: 1000 * 60 * 30,
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      // Same language first (offers are market-agnostic now).
      const { data, error } = await supabase
        .from("catalog_products")
        .select("*")
        .eq("language", language)
        .order("sort_order");
      if (error) throw error;
      if (data && data.length > 0) return data;

      // Last fallback: all products (legacy data without language tags)
      const { data: all } = await supabase
        .from("catalog_products")
        .select("*")
        .order("sort_order");
      return all || [];
    },
  });
};

export const useCatalogProduct = (slug: string) => {
  const { language } = useLocale();
  return useQuery({
    queryKey: ["catalog-product", slug, language],
    staleTime: 1000 * 60 * 30,
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      // Try to fetch the product variant matching the current language first;
      // fall back to any variant with the same slug (slugs are unified across languages).
      const { data: matched } = await supabase
        .from("catalog_products")
        .select("*")
        .eq("slug", slug)
        .eq("language", language)
        .maybeSingle();
      if (matched) return matched;
      const { data, error } = await supabase
        .from("catalog_products")
        .select("*")
        .eq("slug", slug)
        .order("language", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
};

const resolveCatalogFamilyProductIds = async (productId: string) => {
  const { data: currentProduct, error: currentError } = await supabase
    .from("catalog_products")
    .select("id, hotmart_product_id")
    .eq("id", productId)
    .maybeSingle();

  if (currentError) throw currentError;
  if (!currentProduct?.hotmart_product_id) return [productId];

  const { data: familyProducts, error: familyError } = await supabase
    .from("catalog_products")
    .select("id")
    .eq("hotmart_product_id", currentProduct.hotmart_product_id);

  if (familyError) throw familyError;

  const ids = familyProducts?.map((product) => product.id).filter(Boolean) ?? [];
  return ids.length > 0 ? Array.from(new Set(ids)) : [productId];
};

// ── Blocks ──
export const useCatalogBlocks = (productId: string | undefined, includeProductFamily = false) =>
  useQuery({
    queryKey: ["catalog-blocks", productId, includeProductFamily],
    staleTime: 1000 * 60 * 30,
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const productIds = includeProductFamily
        ? await resolveCatalogFamilyProductIds(productId!)
        : [productId!];

      const { data, error } = await supabase
        .from("catalog_blocks")
        .select("*")
        .in("product_id", productIds)
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    enabled: !!productId,
  });

// ── Files ──
export const useCatalogFiles = (productId: string | undefined, blockId?: string | null, includeProductFamily = false) =>
  useQuery({
    queryKey: ["catalog-files", productId, blockId, includeProductFamily],
    staleTime: 1000 * 60 * 30,
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const productIds = includeProductFamily
        ? await resolveCatalogFamilyProductIds(productId!)
        : [productId!];

      let q = supabase
        .from("catalog_files")
        .select("*")
        .in("product_id", productIds)
        .order("sort_order");

      if (blockId === null) {
        q = q.is("block_id", null); // loose files
      } else if (blockId) {
        q = q.eq("block_id", blockId);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
    enabled: !!productId,
  });

export const useCatalogFileById = (fileId: string | undefined) =>
  useQuery({
    queryKey: ["catalog-file", fileId],
    staleTime: 1000 * 60 * 30,
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalog_files")
        .select("*")
        .eq("id", fileId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!fileId,
  });

// ── Admin: all products deduplicated (no language/market filter) ──
export const useAllCatalogProducts = () =>
  useQuery({
    queryKey: ["catalog-products-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalog_products")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      if (!data) return [];
      // Deduplicate by hotmart_product_id — prefer ES row as canonical
      const seen = new Map<string, CatalogProduct>();
      const noId: CatalogProduct[] = [];
      for (const p of data) {
        if (!p.hotmart_product_id) { noId.push(p); continue; }
        const existing = seen.get(p.hotmart_product_id);
        if (!existing || p.language === "es") seen.set(p.hotmart_product_id, p);
      }
      return [...seen.values(), ...noId].sort((a, b) => a.sort_order - b.sort_order);
    },
  });

// ── Admin Mutations ──
export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { slug: string; title: string; hotmart_product_id?: string; checkout_url?: string; image_path?: string }) => {
      const { data, error } = await supabase.from("catalog_products").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-products"] }),
  });
};

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CatalogProduct> & { id: string }) => {
      const { error } = await supabase.from("catalog_products").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-products"] });
      qc.invalidateQueries({ queryKey: ["catalog-products-all"] });
    },
  });
};

// Admin: update title+image across ALL rows sharing the same hotmart_product_id
export const useUpdateProductFamily = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, hotmart_product_id, ...updates }: Partial<CatalogProduct> & { id: string }) => {
      if (hotmart_product_id) {
        const { error } = await supabase
          .from("catalog_products")
          .update(updates)
          .eq("hotmart_product_id", hotmart_product_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("catalog_products").update(updates).eq("id", id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog-products"] });
      qc.invalidateQueries({ queryKey: ["catalog-products-all"] });
    },
  });
};

export const useDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("catalog_products").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-products"] }),
  });
};

export const useCreateBlock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { product_id: string; slug: string; title: string; image_path?: string }) => {
      const { data, error } = await supabase.from("catalog_blocks").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-blocks"] }),
  });
};

export const useUpdateBlock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CatalogBlock> & { id: string }) => {
      const { error } = await supabase.from("catalog_blocks").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-blocks"] }),
  });
};

export const useDeleteBlock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("catalog_blocks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-blocks"] }),
  });
};

export const useCreateFile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { product_id: string; block_id?: string | null; title: string; file_type: string; storage_path?: string; external_url?: string }) => {
      const { data, error } = await supabase.from("catalog_files").insert(input).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-files"] }),
  });
};

export const useUpdateFile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CatalogFile> & { id: string }) => {
      const { error } = await supabase.from("catalog_files").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-files"] }),
  });
};

export const useDeleteFile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("catalog_files").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-files"] }),
  });
};

// ── Banners ──
export type Banner = Tables<"banners">;

export const useBanners = () => {
  const { language, market } = useLocale();
  return useQuery({
    queryKey: ["banners", language, market],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("active", true)
        .eq("language", language)
        .order("sort_order");
      if (error) throw error;
      // Do NOT fall back to ES banners — this causes mixed-language screens
      if (!data || data.length === 0) {
        return [];
      }
      return data;
    },
  });
};

export const useRecetasBanners = () => {
  const { language } = useLocale();
  return useQuery({
    queryKey: ["banners-recetas", language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("active", true)
        .eq("show_on_recetas", true)
        .eq("language", language)
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });
};

export const useAllBanners = () =>
  useQuery({
    queryKey: ["banners-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });

export const useCreateBanner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; image_path?: string | null; link_url?: string | null; sort_order?: number; active?: boolean; show_on_recetas?: boolean }) => {
      const { data, error } = await supabase.from("banners").insert(input as any).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["banners"] }); qc.invalidateQueries({ queryKey: ["banners-all"] }); qc.invalidateQueries({ queryKey: ["banners-recetas"] }); },
  });
};

export const useUpdateBanner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Banner> & { id: string; show_on_recetas?: boolean }) => {
      const { error } = await supabase.from("banners").update(updates as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["banners"] }); qc.invalidateQueries({ queryKey: ["banners-all"] }); qc.invalidateQueries({ queryKey: ["banners-recetas"] }); },
  });
};

export const useDeleteBanner = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("banners").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["banners"] }); qc.invalidateQueries({ queryKey: ["banners-all"] }); },
  });
};

export const getBannerImageUrl = (imagePath: string | null) => {
  if (!imagePath) return null;
  const { data } = supabase.storage.from("product-images").getPublicUrl(imagePath);
  return data.publicUrl;
};

// ── Reorder helpers ──
export const useReorderProducts = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      for (const item of items) {
        const { error } = await supabase.from("catalog_products").update({ sort_order: item.sort_order }).eq("id", item.id);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-products"] }),
  });
};

export const useReorderBlocks = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      for (const item of items) {
        const { error } = await supabase.from("catalog_blocks").update({ sort_order: item.sort_order }).eq("id", item.id);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-blocks"] }),
  });
};

export const useReorderFiles = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      for (const item of items) {
        const { error } = await supabase.from("catalog_files").update({ sort_order: item.sort_order }).eq("id", item.id);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog-files"] }),
  });
};

export const useReorderBanners = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      for (const item of items) {
        const { error } = await supabase.from("banners").update({ sort_order: item.sort_order }).eq("id", item.id);
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["banners"] }); qc.invalidateQueries({ queryKey: ["banners-all"] }); },
  });
};

// ── Translations ──
export const useCatalogFileTranslation = (fileId: string | undefined, language: string) =>
  useQuery({
    queryKey: ["catalog-file-translation", fileId, language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("catalog_file_translations" as any)
        .select("title, content_markdown")
        .eq("file_id", fileId!)
        .eq("language", language)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as { title: string | null; content_markdown: string | null } | null;
    },
    enabled: !!fileId && language !== "es",
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });

// ── Helpers ──
export const getProductImageUrl = (imagePath: string | null) => {
  if (!imagePath) return null;
  const { data } = supabase.storage.from("product-images").getPublicUrl(imagePath);
  return data.publicUrl;
};

export const getSignedFileUrl = async (storagePath: string) => {
  const { data, error } = await supabase.storage
    .from("content-files")
    .createSignedUrl(storagePath, 3600);
  if (error) throw error;
  return data.signedUrl;
};
